import { useCallback, useMemo, useEffect } from 'react';
import extractionStore from '../../../../store';
import { areAllFieldsSelected, mergeExtractionFields } from '../utils/fieldUtils';

const _requiredFields = [
  {
    name: 'Fund name',
    uuid: null,
    id: 'fund name',
    description:
      'The legal name of the AIF (no standard). In the case of umbrella funds, the names of the umbrella fund and the sub-funds should be reported.',
    is_custom: false,
    type: 'string',
    values: [],
    isEmpty: true
  }
];

/**
 * useFieldSelection - Custom hook for managing field selection/extraction logic
 * @param {Array} requiredFields - Required fields array
 * @param {Array} customFields - Custom fields array
 * @param {boolean} isLookup - Whether this is a lookup table
 * @param {string} tableName - Table name
 * @param {Object} category - Category object
 * @param {boolean} isCustom - Whether custom report type
 * @returns {Object} Field selection state and handlers
 */
export const useFieldSelection = (
  requiredFields,
  customFields,
  isLookup,
  tableName,
  category,
  isCustom
) => {
  const { selectedExtractItems: selectedExtract, setSelectedExtract } = extractionStore;

  const mutatedRequiredFields = useMemo(() => {
    if (isCustom) return [];
    return [..._requiredFields];
  }, [isCustom]);

  const isAllFieldsSelected = areAllFieldsSelected(selectedExtract, requiredFields, customFields);

  useEffect(() => {
    if (isLookup) {
      // Only initialize pre-selection when there is no existing selection.
      // This prevents background category refetches or table structure changes
      if (selectedExtract.length > 0) return;

      const preSelectedFields = [];

      category?.tables?.forEach(table => {
        table.columns?.forEach(col => {
          if (col.isEmpty === false) {
            preSelectedFields.push({ ...col, table: table.name });
          }
        });
      });

      setSelectedExtract(preSelectedFields);
      return;
    }

    const _columns =
      category?.tables?.flatMap(item => item.columns)?.filter(item => item.is_custom === false) ||
      [];

    const filteredFields = [
      ..._columns.filter(field => field.isEmpty === false),
      ...customFields.filter(field => field.isEmpty === false)
    ];

    const hasFundColumn = !!filteredFields.find(item => item.id === 'fund name');

    const nextSelectedExtract =
      _columns.length === 0
        ? [...mutatedRequiredFields]
        : hasFundColumn
          ? [...filteredFields]
          : [...filteredFields, ...mutatedRequiredFields];

    const mergedExtractionFields = mergeExtractionFields(nextSelectedExtract, selectedExtract);

    setSelectedExtract(mergedExtractionFields);
  }, [category?.name, category?.tables, isLookup]);

  const onToggleFields = useCallback(() => {
    if (isAllFieldsSelected && !isLookup) {
      setSelectedExtract([...mutatedRequiredFields]);
    } else if (isAllFieldsSelected) {
      setSelectedExtract([]);
    } else if (!isLookup) {
      setSelectedExtract([...customFields, ...requiredFields]);
    } else {
      let _fields = [];

      category?.tables?.forEach(table => {
        _fields = [
          ..._fields,
          ...table.columns.map(item => ({
            ...item,
            table: table.name
          }))
        ];
      });
      setSelectedExtract(_fields);
    }
  }, [
    requiredFields,
    customFields,
    selectedExtract,
    isAllFieldsSelected,
    isLookup,
    category?.tables,
    mutatedRequiredFields,
    setSelectedExtract
  ]);

  const onSelectExtract = useCallback(
    value => {
      const item = selectedExtract.find(
        item =>
          (item.uuid || item.id) === (value.uuid || value.id) &&
          (!item.table || item.table === (value.table || tableName))
      );

      if (item && isLookup) {
        setSelectedExtract(
          selectedExtract.filter(
            item =>
              !((item.uuid || item.id) === (value.uuid || value.id) && item.table === value.table)
          )
        );
      } else if (item) {
        setSelectedExtract(
          selectedExtract.filter(item => (item.uuid || item.id) !== (value.uuid || value.id))
        );
      } else {
        setSelectedExtract([...selectedExtract, value]);
      }
    },
    [selectedExtract, isLookup, tableName, setSelectedExtract]
  );

  return {
    selectedExtract,
    setSelectedExtract,
    isAllFieldsSelected,
    onToggleFields,
    onSelectExtract
  };
};
